import type { ComponentType, SVGProps } from 'react'
import { useTranslation } from 'react-i18next'
import { DocxIcon, GenericFileIcon, PdfIcon, PptIcon, TxtIcon, XlsxIcon } from '../../assets/icons'
import type { SupportedFileType } from '../../types'

type SupportedFileIconType = SupportedFileType | 'ppt' | 'xlsx' | 'txt'

const iconMap: Record<SupportedFileIconType, ComponentType<SVGProps<SVGSVGElement>>> = {
  doc: DocxIcon,
  docx: DocxIcon,
  pdf: PdfIcon,
  ppt: PptIcon,
  xlsx: XlsxIcon,
  txt: TxtIcon,
}

const extensionTypeMap: Record<string, SupportedFileIconType> = {
  doc: 'doc',
  docx: 'docx',
  pdf: 'pdf',
  pdfa: 'pdf',
  ppt: 'ppt',
  pptx: 'ppt',
  xls: 'xlsx',
  xlsx: 'xlsx',
  csv: 'xlsx',
  txt: 'txt',
}

interface FileIconProps {
  fileType: string
  height?: string
  width?: string
}

const FILE_TYPE_DESCRIPTION_KEYS: Record<SupportedFileIconType, string> = {
  doc: 'files.type.doc',
  docx: 'files.type.docx',
  pdf: 'files.type.pdf',
  ppt: 'files.type.ppt',
  xlsx: 'files.type.xlsx',
  txt: 'files.type.default',
}

const FileIcon = ({ fileType, height, width }: FileIconProps) => {
  const { t } = useTranslation()

  const normalizedType = fileType.trim().toLowerCase().replace(/^\./, '')
  const resolvedType = extensionTypeMap[normalizedType]

  if (!resolvedType) {
    return (
      <GenericFileIcon aria-label={t('files.type.default')} role="img" style={{ height, width }} />
    )
  }

  const description = t(FILE_TYPE_DESCRIPTION_KEYS[resolvedType])
  const IconComponent = iconMap[resolvedType]

  return <IconComponent aria-label={description} role="img" style={{ height, width }} />
}

export { FileIcon }
